import { useCallback, useEffect, useRef, useState } from 'react';
import { usePublicConfig } from '@context/PublicConfigContext';

const GSI_SCOPE = 'openid email profile';

/** Multi-color Google "G" mark, sized via the parent font-size container. */
const GoogleGlyph = ({ className = 'h-[18px] w-[18px]' }) => (
  <svg className={className} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path
      fill="#EA4335"
      d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.99 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"
    />
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.13-.84 2.09-1.79 2.74l2.84 2.2c1.66-1.53 2.75-3.79 2.75-6.59z"
    />
    <path
      fill="#FBBC05"
      d="M3.88 10.78a5.4 5.4 0 0 1 0-3.55L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.46-.81 5.95-2.18l-2.84-2.2c-.79.53-1.81.84-3.11.84-2.38 0-4.4-1.57-5.13-3.74L.96 13.04C2.44 15.98 5.48 18 9 18z"
    />
  </svg>
);

/**
 * Custom "Continue with Google" button (no GSI iframe).
 * Uses the OAuth code flow via `google.accounts.oauth2.initCodeClient`,
 * passes the auth code up via `onSuccess(code)` so the server can exchange
 * it for an ID token through the existing /auth/google endpoint.
 */
export const GoogleSignInButton = ({
  onSuccess,
  onError,
  label = 'Continue with Google',
  loading = false,
  disabled = false,
  className = '',
}) => {
  const { googleClientId } = usePublicConfig();
  const clientRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [scriptReady, setScriptReady] = useState(
    () => typeof window !== 'undefined' && !!window.google?.accounts?.oauth2,
  );

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.google?.accounts?.oauth2) {
      setScriptReady(true);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setInterval(() => {
      if (cancelled) return;
      if (window.google?.accounts?.oauth2) {
        setScriptReady(true);
        window.clearInterval(timer);
      }
    }, 150);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!scriptReady || !googleClientId) return;
    clientRef.current = window.google.accounts.oauth2.initCodeClient({
      client_id: googleClientId,
      scope: GSI_SCOPE,
      ux_mode: 'popup',
      callback: (response) => {
        if (response?.code) {
          onSuccessRef.current?.(response.code);
          return;
        }
        if (response?.error) onErrorRef.current?.(response);
      },
      error_callback: (err) => {
        onErrorRef.current?.(err);
      },
    });
  }, [scriptReady, googleClientId]);

  const handleClick = useCallback(() => {
    if (!clientRef.current) return;
    try {
      clientRef.current.requestCode();
    } catch (err) {
      onErrorRef.current?.(err);
    }
  }, []);

  const isDisabled = disabled || loading || !scriptReady || !googleClientId;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={label}
      className={[
        'group relative flex w-full items-center justify-center gap-3 rounded-full border px-4 py-3 text-sm font-semibold transition-all duration-200',
        'border-surface-200 bg-white text-surface-800 shadow-sm hover:border-surface-300 hover:shadow active:scale-[0.99]',
        'dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:border-white/20 dark:hover:bg-white/[0.08]',
        'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/20 dark:focus-visible:ring-brand-400/30',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      ].join(' ')}
    >
      {loading ? (
        <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-current/30 border-t-current" />
      ) : (
        <GoogleGlyph />
      )}
      <span className="truncate">{loading ? 'Signing in…' : label}</span>
    </button>
  );
};

export default GoogleSignInButton;
